import React, { useState, useMemo } from "react";
import { Block } from "../../../../types/sprintLessonTypes";
import { useSprintWebSocket } from "../../../context/SprintWebSocketContext";
import { useFeedbackBlock, useSetUserActionPayload, useUserActionPayload } from "../../../store/multiPageLessonStore";
import { css } from "@emotion/css";

interface MCQBlockRendererProps {
  block: Block;
}

export const MCQBlockRenderer: React.FC<MCQBlockRendererProps> = ({
  block,
}) => {
  // Use state to track the selected option (now properly at the top level of component)
  const [selectedOption, setSelectedOption] = useState<number | null>(null);
  
  const setUserActionPayload = useSetUserActionPayload();
  const userActionPayload = useUserActionPayload();

  // Subscribe to feedbackBlock changes using the block.index_key as namespace
  const feedbackBlock = useFeedbackBlock(block.index_key);

  // This has been commented out because feedback is now handled by the feedbackModal
  // Prepare feedback content if a feedback block exists
  //   const feedbackContent = useMemo(() => {
  //     if (!feedbackBlock) return null;

  //     // Handle MCQ feedback block specifically if it's that type
  //     if (feedbackBlock.block_type === "mcq_feedback" && feedbackBlock.content) {
  //       // For MCQ feedback, extract the relevant fields
  //       const { feedback, poem } = feedbackBlock.content as any;
  //       return (
  //         <div
  //           css={css`
  //             margin-top: 1.5rem;
  //             padding: 1rem;
  //             background: rgba(0, 0, 0, 0.2);
  //             border-radius: 0.5rem;
  //             border-left: 4px solid #4a9eff;
  //           `}
  //         >
  //           <h4>Feedback</h4>
  //           <p>{feedback}</p>
  //           {poem && (
  //             <>
  //               <h4>Reflective Poem</h4>
  //               <p
  //                 css={css`
  //                   font-style: italic;
  //                 `}
  //               >
  //                 {poem}
  //               </p>
  //             </>
  //           )}
  //         </div>
  //       );
  //     }

  //     // Generic fallback for other block types
  //     return (
  //       <div
  //         css={css`
  //           margin-top: 1.5rem;
  //           padding: 1rem;
  //           background: rgba(0, 0, 0, 0.2);
  //           border-radius: 0.5rem;
  //           border-left: 4px solid #4a9eff;
  //           white-space: pre-wrap;
  //         `}
  //       >
  //         <h4>Feedback</h4>
  //         <pre
  //           css={css`
  //             overflow-x: auto;
  //             font-family: monospace;
  //             font-size: 0.9rem;
  //           `}
  //         >
  //           {JSON.stringify(feedbackBlock.content, null, 2)}
  //         </pre>
  //       </div>
  //     );
  //   }, [feedbackBlock]);

  // Get the question for aria labels
  const question = block.content?.question || "Multiple choice question";

  // CSS styles for the option boxes
  const optionContainerStyle = css`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    margin-top: 1rem;
  `;

  const optionBoxStyle = css`
    padding: 1rem 1.5rem;
    border-radius: 0.5rem;
    border: 1px solid #3d3d4d;
    background-color: #2d2d3a;
    color: #e0e0e0;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
      background-color: #3d3d4d;
      transform: translateY(-1px);
    }

    &:active {
      transform: translateY(0);
    }
  `;

  const selectedOptionStyle = css`
    background-color: #4f46e5;
    border-color: #4f46e5;
    color: white;

    &:hover {
      background-color: #4338ca;
    }
  `;

  const handleOptionClick = (index: number, option: string) => {
    // Allow changing the selected option by removing the early return
    
    setSelectedOption(index);

    // Store MCQ selection in userActionPayload instead of sending WebSocket message directly
    // This will be sent when the user clicks the "Check" button on either the NavigationButtons (or the FeedbackModal if we later change it)
    const payload = new Map(userActionPayload);
    payload.set(block.index_key, {
      type: "mcq_feedback",
      block: block,
      selected_option: index,
      option_text: option,
      // Store feedback information so the Check button can show feedback
      showFeedback: true,
      feedbackNamespace: block.index_key,
    });
    setUserActionPayload(payload);

    // console.log(`Selected option ${index}: ${option}`);
  };

  return (
    <div>
      <div
        id={`${block.block_id}-question`}
        className={css`
          font-weight: 600;
          margin-bottom: 0.5rem;
        `}
      >
        {question}
      </div>
      <div
        className={optionContainerStyle}
        role="radiogroup"
        aria-labelledby={`${block.block_id}-question`}
      >
        {block.content?.options?.map((option: string, index: number) => {
          const isSelected = selectedOption === index;
          const optionId = `${block.block_id}-option-${index}`;

          // Combine styles based on state
          const boxStyle = css`
            ${optionBoxStyle}
            ${isSelected ? selectedOptionStyle : ""}
          `;

          return (
            <div
              key={optionId}
              id={optionId}
              className={boxStyle}
              onClick={() => handleOptionClick(index, option)}
              role="radio"
              aria-checked={isSelected}
              aria-labelledby={`${optionId}-label`}
              tabIndex={0}
            >
              <span id={`${optionId}-label`}>{option}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
